// Servicio de Ventas
// Gestiona las ventas registradas desde pedidos

import { getCollection, getDocument, createDocument, updateDocument, deleteDocument } from './firestore.service'
import { Timestamp } from 'firebase/firestore'
import { getBusinessRules } from './businessRules.service'
import { notifyOrderDelivered } from './notifications.service'
import { applyStockMovement } from './stock.service'
import { getAllWarehouses } from './warehouses.service'
import { normalizeStockColor, normalizeStockSize } from '../utils/stock'

const SALES_COLLECTION = 'sales'

const resolveSaleWarehouseId = async (fulfillment, fallbackWarehouseId = null) => {
  const directPickupId = typeof fulfillment?.pickupWarehouseId === 'string'
    ? fulfillment.pickupWarehouseId.trim()
    : ''
  if (directPickupId) return directPickupId
  if (fallbackWarehouseId && String(fallbackWarehouseId).trim()) return String(fallbackWarehouseId).trim()
  const warehouses = await getAllWarehouses()
  return warehouses.length > 0 ? warehouses[0].id : null
}

const getItemStockSelection = (item) => {
  const selection = item?.variant?.selection && typeof item.variant.selection === 'object'
    ? item.variant.selection
    : {}
  const variantType = String(item?.variant?.type || '').trim().toLowerCase()
  const rawColor = selection.color || selection.colorHex || selection.colorValue || selection.hex || null
  const rawSize = selection.size || selection.talle || selection.talla || null
  const fallbackColor = variantType === 'color' ? item?.variant?.name || null : null
  const fallbackSize = variantType === 'size' ? item?.variant?.name || null : null
  return {
    color: normalizeStockColor(rawColor || fallbackColor),
    size: normalizeStockSize(rawSize || fallbackSize)
  }
}

const applySaleStockMovements = async ({ saleNumber, items, fulfillment, warehouseId, type, userId, reasonPrefix }) => {
  const safeItems = Array.isArray(items) ? items : []
  if (safeItems.length === 0) return warehouseId || null
  const resolvedWarehouseId = await resolveSaleWarehouseId(fulfillment, warehouseId)
  if (!resolvedWarehouseId) {
    throw new Error('No hay depósito activo para actualizar stock')
  }
  for (const item of safeItems) {
    const productId = (item?.productId || '').trim()
    const quantity = Number(item?.quantity || 0) || 0
    if (!productId || quantity <= 0) continue
    const selection = getItemStockSelection(item)
    await applyStockMovement({
      productId,
      warehouseId: resolvedWarehouseId,
      type,
      quantity,
      reason: `${reasonPrefix} ${saleNumber}`,
      userId: userId || null,
      size: selection.size,
      color: selection.color
    })
  }
  return resolvedWarehouseId
}

/**
 * Generar número único de venta
 * @returns {string} Número de venta (VTA-YYYY-XXX)
 */
export const generateSaleNumber = async () => {
  try {
    const year = new Date().getFullYear()
    const sales = await getCollection(SALES_COLLECTION, [
      { field: 'saleNumber', operator: '>=', value: `VTA-${year}-000` },
      { field: 'saleNumber', operator: '<=', value: `VTA-${year}-999` }
    ])
    
    const numbers = sales
      .map(s => {
        const match = s.saleNumber?.match(/VTA-\d{4}-(\d{3})/)
        return match ? parseInt(match[1]) : 0
      })
      .filter(n => n > 0)
      .sort((a, b) => b - a)
    
    const nextNumber = numbers.length > 0 ? numbers[0] + 1 : 1
    return `VTA-${year}-${String(nextNumber).padStart(3, '0')}`
  } catch (error) {
    console.error('Error generando número de venta:', error)
    throw error
  }
}

/**
 * Crear una nueva venta
 * @param {Object} saleData - Datos de la venta
 * @returns {Promise<string>} ID de la venta creada
 */
export const createSale = async (saleData) => {
  try {
    const saleNumber = await generateSaleNumber()
    
    const data = {
      saleNumber,
      orderId: saleData.orderId,
      orderNumber: saleData.orderNumber,
      customer: saleData.customer || {},
      items: saleData.items || [],
      subtotal: saleData.subtotal || 0,
      discount: saleData.discount || 0,
      taxes: saleData.taxes || 0,
      shipping: saleData.shipping || 0,
      total: saleData.total || 0,
      paymentMethod: saleData.paymentMethod || 'efectivo',
      payment: saleData.payment || null,
      paymentGateway: saleData.paymentGateway || null,
      paymentAdjustment: saleData.paymentAdjustment || null,
      fulfillment: saleData.fulfillment || null,
      paymentProofs: saleData.paymentProofs || [],
      saleDate: saleData.saleDate instanceof Timestamp 
        ? saleData.saleDate 
        : Timestamp.fromDate(new Date(saleData.saleDate || new Date())),
      notes: saleData.notes || '',
      status: saleData.status || 'pending',
      paid: saleData.paid !== undefined ? saleData.paid : false, // Estado de pago
      createdAt: Timestamp.now(),
      createdBy: saleData.createdBy || null,
      createdByName: saleData.createdByName || 'Sistema',
      updatedAt: Timestamp.now()
    }
    
    const saleId = await createDocument(SALES_COLLECTION, data)
    const shouldAdjustStock = saleData.applyStockMovements ?? data.paid === true

    if (shouldAdjustStock) {
      const warehouseId = await applySaleStockMovements({
        saleNumber,
        items: data.items,
        fulfillment: data.fulfillment,
        warehouseId: null,
        type: 'salida',
        userId: data.createdBy || null,
        reasonPrefix: 'Venta'
      })

      await updateDocument(SALES_COLLECTION, saleId, {
        stockAdjustedAt: Timestamp.now(),
        stockWarehouseId: warehouseId || null
      })
    }

    return saleId
  } catch (error) {
    console.error('Error creando venta:', error)
    throw error
  }
}

/**
 * Obtener una venta por ID
 * @param {string} saleId - ID de la venta
 * @returns {Promise<Object>} Venta
 */
export const getSale = async (saleId) => {
  try {
    return await getDocument(SALES_COLLECTION, saleId)
  } catch (error) {
    console.error('Error obteniendo venta:', error)
    throw error
  }
}

/**
 * Obtener todas las ventas
 * @param {Object} filters - Filtros opcionales
 * @returns {Promise<Array>} Lista de ventas
 */
export const getAllSales = async (filters = {}) => {
  try {
    const firestoreFilters = []
    
    if (filters.status) {
      firestoreFilters.push({ field: 'status', operator: '==', value: filters.status })
    }
    
    if (filters.paymentMethod) {
      firestoreFilters.push({ field: 'paymentMethod', operator: '==', value: filters.paymentMethod })
    }
    
    // Obtener todas las ventas y ordenar en el cliente
    const sales = await getCollection(SALES_COLLECTION, firestoreFilters)
    
    // Filtrar por fecha si es necesario
    let filteredSales = sales
    if (filters.startDate || filters.endDate) {
      filteredSales = sales.filter(sale => {
        const saleDate = sale.saleDate?.toDate ? sale.saleDate.toDate() : new Date(sale.saleDate)
        if (filters.startDate && saleDate < new Date(filters.startDate)) return false
        if (filters.endDate && saleDate > new Date(filters.endDate)) return false
        return true
      })
    }
    
    // Ordenar por fecha de venta (más recientes primero)
    filteredSales.sort((a, b) => {
      const aTime = a.saleDate?.toDate ? a.saleDate.toDate() : new Date(a.saleDate)
      const bTime = b.saleDate?.toDate ? b.saleDate.toDate() : new Date(b.saleDate)
      return bTime - aTime
    })
    
    return filteredSales
  } catch (error) {
    console.error('Error obteniendo ventas:', error)
    throw error
  }
}

/**
 * Obtener venta por ID de pedido
 * @param {string} orderId - ID del pedido
 * @returns {Promise<Object|null>} Venta o null
 */
export const getSaleByOrderId = async (orderId) => {
  try {
    const sales = await getCollection(SALES_COLLECTION, [
      { field: 'orderId', operator: '==', value: orderId }
    ])
    return sales.length > 0 ? sales[0] : null
  } catch (error) {
    console.error('Error obteniendo venta por orderId:', error)
    throw error
  }
}

/**
 * Actualizar una venta
 * @param {string} saleId - ID de la venta
 * @param {Object} updates - Campos a actualizar
 * @returns {Promise<void>}
 */
export const updateSale = async (saleId, updates) => {
  try {
    const rules = await getBusinessRules()
    const mode = rules?.ordersSalesMode?.mode || 'linked_dependent'

    const data = {
      ...updates,
      updatedAt: Timestamp.now()
    }
    
    // Convertir fecha si es necesario
    if (data.saleDate && !(data.saleDate instanceof Timestamp)) {
      data.saleDate = Timestamp.fromDate(new Date(data.saleDate))
    }
    
    let currentSale = null
    if (data.paid === true && (mode === 'linked_dependent' || mode === 'sales_only')) {
      try {
        currentSale = await getSale(saleId)
        if (mode === 'sales_only') {
          data.status = 'completed'
        } else if (currentSale?.orderId) {
          const { getOrder } = await import('./orders.service')
          const order = await getOrder(currentSale.orderId)
          if (order?.status === 'delivered') {
            data.status = 'completed'
          } else {
            data.status = 'pending_delivery'
          }
        }
      } catch (orderError) {
        console.warn('Error verificando estado del pedido:', orderError)
      }
    }
    
    await updateDocument(SALES_COLLECTION, saleId, data)

    const saleForStock = currentSale || await getSale(saleId)
    const nextStatus = typeof data.status === 'string' ? data.status : saleForStock?.status
    const shouldApplyStock = data.paid === true &&
      saleForStock &&
      !saleForStock.stockAdjustedAt &&
      nextStatus !== 'cancelled'
    const shouldRestoreStock = saleForStock &&
      saleForStock.status !== 'cancelled' &&
      nextStatus === 'cancelled' &&
      saleForStock.stockAdjustedAt &&
      !saleForStock.stockRestoredAt

    if (shouldApplyStock) {
      const adjustedWarehouseId = await applySaleStockMovements({
        saleNumber: saleForStock.saleNumber || saleForStock.id,
        items: saleForStock.items,
        fulfillment: saleForStock.fulfillment,
        warehouseId: saleForStock.stockWarehouseId || null,
        type: 'salida',
        userId: data.updatedBy || data.updatedByName || null,
        reasonPrefix: 'Venta'
      })
      await updateDocument(SALES_COLLECTION, saleId, {
        stockAdjustedAt: Timestamp.now(),
        stockWarehouseId: adjustedWarehouseId || saleForStock.stockWarehouseId || null
      })
    }

    if (shouldRestoreStock) {
      const restoredWarehouseId = await applySaleStockMovements({
        saleNumber: saleForStock.saleNumber || saleForStock.id,
        items: saleForStock.items,
        fulfillment: saleForStock.fulfillment,
        warehouseId: saleForStock.stockWarehouseId || null,
        type: 'entrada',
        userId: data.updatedBy || data.updatedByName || null,
        reasonPrefix: 'Cancelación venta'
      })
      await updateDocument(SALES_COLLECTION, saleId, {
        stockRestoredAt: Timestamp.now(),
        stockWarehouseId: restoredWarehouseId || saleForStock.stockWarehouseId || null
      })
    }

    if (mode === 'sales_only' && data.paid === true) {
      try {
        const sale = currentSale || await getSale(saleId)
        if (sale?.orderId) {
          await updateDocument('orders', sale.orderId, { status: 'delivered' })
          notifyOrderDelivered(sale.orderId).catch(() => null)
        }
      } catch (orderError) {
        console.warn('Error actualizando estado del pedido desde la venta:', orderError)
      }
    }

    if (mode === 'linked_dependent') {
      try {
        const sale = currentSale || await getSale(saleId)
        if (sale?.orderId) {
          const { getOrder } = await import('./orders.service')
          const order = await getOrder(sale.orderId)
          if (order) {
            let nextStatus = order.status || 'pending'

            if (data.paid === true && order.status === 'pending') {
              nextStatus = 'confirmed'
            }

            if (data.status === 'completed' && order.status !== 'cancelled') {
              nextStatus = 'delivered'
            }

            if (nextStatus !== order.status) {
              await updateDocument('orders', sale.orderId, { status: nextStatus })
              if (nextStatus === 'delivered') {
                notifyOrderDelivered(sale.orderId).catch(() => null)
              }
            }
          }
        }
      } catch (orderError) {
        console.warn('Error sincronizando estado del pedido desde la venta:', orderError)
      }
    }
  } catch (error) {
    console.error('Error actualizando venta:', error)
    throw error
  }
}

/**
 * Eliminar una venta
 * @param {string} saleId - ID de la venta
 * @returns {Promise<void>}
 */
export const deleteSale = async (saleId) => {
  try {
    await deleteDocument(SALES_COLLECTION, saleId)
  } catch (error) {
    console.error('Error eliminando venta:', error)
    throw error
  }
}

